import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import type { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import type {
	ProjectDetailsData,
	ProjectDetailsRequestBody,
	ProjectDetailsRequestBodySimplified,
} from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/summary/types';
import { fg } from '@atlassian/jira-feature-gating';
import projectService from '../../services/project';
import type { AllProjectDetailsUpdate } from '../../services/project/types';
import type { Action } from '../../state/actions';
import {
	projectContextSelector,
	getOnDetailsUpdated,
	getIsAdmin,
} from '../../state/context/selectors';
import {
	SAVE_ALL_PROJECT_DETAILS,
	saveAllProjectDetailsSuccess,
	saveProjectDetailsFail,
} from '../../state/entities/actions';
import type { AppState } from '../../state/types';
import {
	projectAvatarUiSelector,
	projectAvatarIdUiSelector,
	projectCategoryIdUiSelector,
	projectNameToSaveSelector,
	projectKeyToSaveSelector,
	projectDescriptionToSaveSector,
	projectUrlToSaveSelector,
	simplifiedToSaveSelector,
	releasedProjectKeysSelector,
} from '../../state/ui/selectors';

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (action$: ActionsObservable<Action>, store: MiddlewareAPI<AppState>) =>
	action$.ofType(SAVE_ALL_PROJECT_DETAILS).mergeMap(() => {
		const state = store.getState();
		const onDetailsUpdated = getOnDetailsUpdated(state);

		const simplified = simplifiedToSaveSelector(state);
		const isAdmin = getIsAdmin(state);
		const projectKey = projectKeyToSaveSelector(state);
		const projectName = projectNameToSaveSelector(state);
		const projectUrl = projectUrlToSaveSelector(state);
		const projectDescription = projectDescriptionToSaveSector(state);

		const projectDetailsSelectorSimplified: ProjectDetailsRequestBodySimplified = {
			key: projectKey,
			name: projectName,
			avatarId: projectAvatarIdUiSelector(state),
			projectCategoryId: projectCategoryIdUiSelector(state),
		};

		if (fg('hix-4189-releasing-historical-project-keys')) {
			projectDetailsSelectorSimplified.releasedProjectKeys = releasedProjectKeysSelector(state);
		}

		const projectDetailsSelector: ProjectDetailsRequestBody = {
			name: projectName,
			description: projectDescription,
			url: projectUrl,
			avatarId: projectAvatarIdUiSelector(state),
		};

		if (isAdmin) {
			projectDetailsSelector.key = projectKey;
			if (fg('hix-4189-releasing-historical-project-keys')) {
				projectDetailsSelector.releasedProjectKeys = releasedProjectKeysSelector(state);
			}
			projectDetailsSelector.projectCategoryId = projectCategoryIdUiSelector(state);
		}
		/*
             updateAllProjectDetails - CMP implementation that carries extra props (description and url field to the db).
             updateAllProjectDetailsSimplified - TMP implementation.
         */
		const requestPromise = !simplified
			? projectService.updateAllProjectDetails(
					projectContextSelector(state),
					projectDetailsSelector,
				)
			: projectService.updateAllProjectDetailsSimplified(
					projectContextSelector(state),
					projectDetailsSelectorSimplified,
				);

		const updatedHistoricalProjectKeys = (newHistoricalProjectKeys?: string[]) => {
			if (!fg('hix-4189-releasing-historical-project-keys')) {
				return [];
			}

			return newHistoricalProjectKeys !== undefined
				? newHistoricalProjectKeys
				: state.entities.historicalProjectKeys;
		};

		// The endpoint returns avatarId ... but we need AvatarDetails (avatar ID + URL)
		// fortunately we already have that in UI state so let's use that and skip one useless HTTP call
		return Observable.fromPromise(requestPromise)
			.do(({ key, name, description, avatarId, url }: ProjectDetailsData) => {
				onDetailsUpdated({
					key,
					name,
					description,
					avatarId,
					url,
				});
			})
			.map((restResponse: ProjectDetailsData) => {
				const { avatarId, id, isSimplified, accessLevel, projectCategoryId, ...rest } =
					restResponse;
				const actionPayload: AllProjectDetailsUpdate = {
					...rest,
					simplified: isSimplified,
					avatar: projectAvatarUiSelector(state),
					accessLevel: accessLevel.value,
					projectCategoryId,
					historicalProjectKeys: updatedHistoricalProjectKeys(rest.historicalProjectKeys),
				};

				return saveAllProjectDetailsSuccess(actionPayload);
			})
			.catch((error) => Observable.of(saveProjectDetailsFail(error)));
	});
